import { useEffect, useRef } from 'react';
import { Image } from 'scanbot-web-sdk/@types/core-types';
import { IDocumentScannerHandle } from 'scanbot-web-sdk/@types/interfaces/i-document-scanner-handle';
import {
  DocumentScannerViewConfiguration,
  TextConfiguration
} from 'scanbot-web-sdk/@types/model/configuration/document-scanner-view-configuration';
import { getScanbotSdk } from '~/lib/scanbot-client';

type Props = {
  text: TextConfiguration;
  onNewPage: (d: Image) => void;
  aspectRatio?: ConstrainDouble;
  enableManualCapture?: boolean;
};

export default function Scanner({
  text,
  onNewPage,
  aspectRatio,
  enableManualCapture
}: Props) {
  const scannerRef = useRef<IDocumentScannerHandle | null>(null);

  const scannerConfig: DocumentScannerViewConfiguration = {
    containerId: 'document-scanner-container',
    text,
    videoConstraints: {
      facingMode: 'environment',
      aspectRatio
    },

    onDocumentDetected: (result) => {
      // This is on the perimeter of the library that does not support typing
      // so we add on our own types
      if (result.croppedImage) {
        onNewPage(result.croppedImage);
      }
    },
    onCaptureButtonClick: async () => {
      if (scannerRef.current) {
        const captured = await scannerRef.current.detectAndCrop();

        // When a cropped image detected we use the cropped image
        if (captured?.croppedImage) {
          onNewPage(captured.croppedImage);
          return;
        }

        // If no cropped image detected then use the original image
        if (captured && enableManualCapture) {
          onNewPage(captured.originalImage);
        }
      }
    }
  };

  // Initialize scanbot in this use effect
  useEffect(() => {
    getScanbotSdk()
      .then((sbsdk) => sbsdk.createDocumentScanner(scannerConfig))
      .then((scanner) => {
        if (scannerRef.current !== null) {
          return;
        }
        scannerRef.current = scanner;
      })
      .catch(console.error);

    return () => {
      scannerRef.current?.dispose();
    };
  }, []);

  return (
    <div
      id="document-scanner-container"
      className="w-full h-full relative"
    ></div>
  );
}
